import React, { useCallback, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth, ACTIVITY_KEY } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const IDLE_MS = 30 * 60 * 1000;
const WARN_MS = 2 * 60 * 1000;
const REFRESH_EVERY_MS = 60 * 1000;
const REFRESH_KEY = "rs_last_refresh";
const EVENTS = ["pointerdown", "keydown", "scroll", "wheel", "touchstart", "mousemove"];

const readTs = (k) => Number(localStorage.getItem(k)) || 0;
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function SessionManager() {
  const { logout } = useAuth();
  const [remaining, setRemaining] = useState(null);
  const warnRef = useRef(false);
  const lastWrite = useRef(0);

  const markActivity = useCallback(() => {
    const now = Date.now();
    lastWrite.current = now;
    localStorage.setItem(ACTIVITY_KEY, String(now));
  }, []);

  const expire = useCallback(() => {
    sessionStorage.setItem("rs_session_expired", "1");
    logout("expired");
  }, [logout]);

  const stay = async () => {
    markActivity();
    warnRef.current = false;
    setRemaining(null);
    localStorage.setItem(REFRESH_KEY, String(Date.now()));
    try { await api.post("/auth/refresh"); } catch { expire(); }
  };

  useEffect(() => {
    markActivity();
    const onActivity = () => {
      if (warnRef.current || Date.now() - lastWrite.current < 5000) return;
      markActivity();
    };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const tick = setInterval(() => {
      const now = Date.now();
      const last = readTs(ACTIVITY_KEY);
      const idle = now - last;
      if (idle >= IDLE_MS) { clearInterval(tick); expire(); return; }
      if (idle >= IDLE_MS - WARN_MS) {
        warnRef.current = true;
        setRemaining(Math.ceil((IDLE_MS - idle) / 1000));
        return;
      }
      if (warnRef.current) { warnRef.current = false; setRemaining(null); }
      const lastRefresh = readTs(REFRESH_KEY);
      if (now - lastRefresh >= REFRESH_EVERY_MS && last > lastRefresh) {
        localStorage.setItem(REFRESH_KEY, String(now));
        api.post("/auth/refresh").catch(() => expire());
      }
    }, 1000);
    return () => {
      clearInterval(tick);
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [markActivity, expire]);

  return (
    <AlertDialog open={remaining !== null}>
      <AlertDialogContent data-testid="session-timeout-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" /> Votre session va expirer
          </AlertDialogTitle>
          <AlertDialogDescription>
            Sans action de votre part, vous serez déconnecté dans{" "}
            <span data-testid="session-timeout-countdown" className="font-mono font-semibold text-slate-100">{fmt(remaining || 0)}</span>{" "}
            pour des raisons de sécurité.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="session-timeout-logout-btn" onClick={expire}>Se déconnecter</AlertDialogCancel>
          <AlertDialogAction data-testid="session-timeout-stay-btn" onClick={stay}>Rester connecté</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
